/* eslint-disable @typescript-eslint/member-ordering */
import { DestroyRef, inject, Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError } from 'rxjs/operators';

import { Actions, createEffect, ofType } from '@ngrx/effects';

import { exhaustMap, of, switchMap, tap } from 'rxjs';
import { Store } from '@ngrx/store';
import { Role, Roles } from '../types';
import { Role as APIRoleInterface } from '@team-management/models/roles';
import { convertKeysToCamelCase } from '@shared/utils';

import { DELETE_DIALOG_DATA } from '@shared/dialogs/types';
import { AdminService } from '@admin/admin.service';

import {
  DEFAULT_DELETE_ROLE_DATA,
  DEFAULT_ELIGIBILITY_DATA,
} from '../constants';
import {
  ROLE_TOAST_ACTION,
  ROLES_ACTION,
  ROLES_DIALOG_ACTION,
} from '../actions';
import { storeRoles } from '@team-management/redux/team-management.actions';
import { DeleteDialogComponent } from '@shared/dialogs/components';
import { EligibilityDialogComponent } from '@data-access/roles';

@Injectable()
export class RolesEffect {
  private readonly _adminService = inject(AdminService);
  private readonly _actions$ = inject(Actions);
  private readonly _store = inject(Store);
  private readonly _snackBar = inject(MatSnackBar);
  private readonly _dialog = inject(MatDialog);
  private readonly _destroyRef = inject(DestroyRef);

  getRoles$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(ROLES_ACTION.loadRoles),
        exhaustMap(() =>
          this._adminService.getAllRoles().pipe(
            tap((roles) => {
              const rData = roles.data as APIRoleInterface[];
              this._store.dispatch(
                storeRoles({
                  level: 'study',
                  roles: rData.filter((role) => role.study_level),
                }),
              );
              this._store.dispatch(
                storeRoles({
                  level: 'site',
                  roles: rData.filter((role) => role.site_level),
                }),
              );
              this._store.dispatch(
                ROLES_ACTION.onLoadRolesSuccess({
                  roles: convertKeysToCamelCase(roles) as unknown as Roles,
                }),
              );
            }),
            catchError((err: Error) =>
              of(ROLES_ACTION.onLoadRolesFailure({ error: err })),
            ),
          ),
        ),
      ),
    {
      dispatch: false,
    },
  );
  getPermission$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(ROLES_ACTION.loadPermissionList),
        exhaustMap(() =>
          this._adminService.getAllPermissionList().pipe(
            tap((permission) =>
              this._store.dispatch(
                ROLES_ACTION.onLoadPermissionListSuccess({
                  labels: permission.labels,
                  permissions: permission.permissions,
                }),
              ),
            ),
            catchError((err: Error) =>
              of(ROLES_ACTION.onLoadPermissionsFailure({ error: err })),
            ),
          ),
        ),
      ),
    {
      dispatch: false,
    },
  );

  createUserRole$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ROLES_ACTION.creatUserRole),
      switchMap(({ role }) =>
        this._adminService.createUserRole(role).pipe(
          switchMap(() => {
            this._store.dispatch(ROLES_ACTION.loadRoles());
            return [
              ROLE_TOAST_ACTION.displayToastMessage({
                message: 'Changes Saved',
              }),
              ROLES_ACTION.onCreateUserRoleSuccess(),
            ];
          }),
          catchError((err: Error) =>
            of(ROLES_ACTION.onCreateUserRoleFailure({ error: err })),
          ),
        ),
      ),
    ),
  );

  fetchUserRole$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ROLES_ACTION.fetchUserRole),
      switchMap(({ roleId }) =>
        this._adminService.fetchCurrentUserRole(roleId).pipe(
          switchMap((response) => {
            return [
              ROLES_ACTION.onFetchUserRoleSuccess({
                currentRole: response,
              }),
            ];
          }),
          catchError((err: Error) =>
            of(ROLES_ACTION.onFetchUserRoleFailure({ error: err })),
          ),
        ),
      ),
    ),
  );

  updateUserRole$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ROLES_ACTION.updateUserRole),
      switchMap(({ roleId, role }) =>
        this._adminService.updateUserRole(roleId, role).pipe(
          switchMap(() => {
            return [
              ROLE_TOAST_ACTION.displayToastMessage({
                message: 'Changes Saved',
              }),
              ROLES_ACTION.onUpdateUSerRoleSuccess(),
            ];
          }),
          catchError((err: Error) =>
            of(ROLES_ACTION.onUpdateUserRoleFailure({ error: err })),
          ),
        ),
      ),
    ),
  );

  deleteUserRole$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ROLES_ACTION.deleteUserRole),
      switchMap(({ role, reasonForDelete }) =>
        this._adminService.deleteUserRole(role?.id, reasonForDelete).pipe(
          switchMap(() => {
            return [
              ROLE_TOAST_ACTION.displayToastMessage({
                message: 'Changes Saved',
              }),
              ROLES_ACTION.onDeleteUserRoleSuccess({
                role: role,
              }),
            ];
          }),
          catchError((err: Error) =>
            of(ROLES_ACTION.onDeleteUserRoleFailure({ error: err })),
          ),
        ),
      ),
    ),
  );

  checkRoleEligibility$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ROLES_ACTION.checkRoleDeleteEligibility),
      switchMap(({ role }) =>
        this._adminService.checkRoleDeleteEligibility(role).pipe(
          switchMap((res) => {
            const component = () =>
              res.canDelete
                ? DeleteDialogComponent
                : EligibilityDialogComponent;
            return [
              ROLES_DIALOG_ACTION.openCheckDeleteEligibilityDialog({
                component,
                data: this._constructDialogData(res.canDelete, role),
              }),
            ];
          }),
        ),
      ),
    ),
  );

  /**
   * @ROLES DIALOG ACTIONS
   * **/
  openCheckDeleteEligibilityDialog$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(ROLES_DIALOG_ACTION.openCheckDeleteEligibilityDialog),
        tap(({ component, data }) => {
          const dialogRef = this._dialog.open(component(), {
            data: data,
          });
          this._customDialogListeners(dialogRef, data.payload as Role);
        }),
      ),
    {
      dispatch: false,
    },
  );

  displayToastMessage$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(ROLE_TOAST_ACTION.displayToastMessage),
        tap(({ message }) => this._snackBarMessage(message)),
      ),
    {
      dispatch: false,
    },
  );

  private _snackBarMessage(message: string, action = ''): void {
    this._snackBar.open(message, action, {
      duration: 5000,
      horizontalPosition: 'start',
      verticalPosition: 'bottom',
    });
  }

  private _customDialogListeners(dialogRef: MatDialogRef<unknown>, role: Role) {
    if (dialogRef) {
      dialogRef
        .afterClosed()
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe({
          next: (data: string) => {
            if (data?.length > 0) {
              this._store.dispatch(
                ROLES_ACTION.deleteUserRole({
                  reasonForDelete: data,
                  role: role,
                }),
              );
            }
          },
        });
    }
  }

  private _constructDialogData(canDelete: boolean, role: Role) {
    if (canDelete) {
      return {
        ...DEFAULT_DELETE_ROLE_DATA,
        description: `Are you sure you want to remove the user role ${role?.name}? if yes, please provide a reason.`,
        payload: role,
        type: 'DELETE',
      } as DELETE_DIALOG_DATA<Role>;
    }
    return {
      ...DEFAULT_ELIGIBILITY_DATA,
    } as DELETE_DIALOG_DATA<Role>;
  }
}
